import React, { useState } from 'react';

import type { ResetPasswordDTO } from '../../services/apiClient';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import Button from '../../components/common/Button';
import FormField from '../../components/common/FormField';
import PasswordRequirements from '../../components/common/PasswordRequirements';
import AuthLayout from '../../components/layout/AuthLayout';
import { useUserApi } from '../../hooks/useUserApi';

const ResetPassword: React.FC = () => {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [newPassword, setNewPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null); // For `newPassword` errors
  const [confirmPasswordError, setConfirmPasswordError] = useState<
    string | null
  >(null); // For `confirmPassword` errors
  const [passwordValidation, setPasswordValidation] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    digit: false,
  });

  const { resetPassword } = useUserApi();

  const location = useLocation();
  const navigate = useNavigate();

  // Extract token and email from the query string
  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get('token');
  const email = queryParams.get('email');

  const handlePasswordChange = (value: string): void => {
    setNewPassword(value);
    setPasswordValidation({
      length: value.length >= 6,
      uppercase: /[A-Z]/.test(value),
      lowercase: /[a-z]/.test(value),
      digit: /\d/.test(value),
    });
    setPasswordError(null); // Clear the error on input change
  };

  const handleConfirmPasswordChange = (value: string): void => {
    setConfirmPassword(value);
    setConfirmPasswordError(null); // Clear the error on input change
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setErrorMessage(null);
    setSuccessMessage(null);

    let isValid = true;

    // Validate new password
    if (
      !passwordValidation.length ||
      !passwordValidation.uppercase ||
      !passwordValidation.lowercase ||
      !passwordValidation.digit
    ) {
      setPasswordError('Password does not meet the required criteria.');
      isValid = false;
    }

    // Validate confirm password
    if (newPassword !== confirmPassword) {
      setConfirmPasswordError('Passwords do not match.');
      isValid = false;
    }

    if (!isValid) {
      return;
    }

    const dto: ResetPasswordDTO = {
      email: email ?? '',
      token: token ?? '',
      newPassword,
    };

    try {
      setIsSubmitting(true);
      const response = await resetPassword(dto);

      if (response && response.success) {
        setSuccessMessage('Your password has been reset successfully.');
        setTimeout(() => {
          navigate('/login'); // Redirect to login after success
        }, 2000);
      } else {
        setErrorMessage('Failed to reset password. Please try again.');
      }
    } catch (error) {
      console.error('Password reset error:', error);
      setErrorMessage('An error occurred. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AuthLayout
      title="Reset your password"
      description="Enter your new password to reset your account password."
    >
      <div className="mt-8">
        {errorMessage && (
          <div className="mb-4 text-sm text-red-600">{errorMessage}</div>
        )}
        {successMessage && (
          <div className="mb-4 text-sm text-green-600">{successMessage}</div>
        )}
        <form onSubmit={handleSubmit}>
          <FormField
            id="newPassword"
            label="New Password"
            type="password"
            value={newPassword}
            onChange={(e) => handlePasswordChange(e.target.value)}
            placeholder="Enter new password"
            error={passwordError ?? ''}
          />
          <PasswordRequirements validation={passwordValidation} />
          <FormField
            id="confirmPassword"
            label="Confirm Password"
            type="password"
            value={confirmPassword}
            onChange={(e) => handleConfirmPasswordChange(e.target.value)}
            placeholder="Confirm new password"
            error={confirmPasswordError ?? ''}
          />
          <div className="mt-6">
            <Button
              type="submit"
              className="w-full"
              disabled={isSubmitting}
              isLoading={isSubmitting}
            >
              Reset Password
            </Button>
          </div>
        </form>
        <p className="mt-6 text-sm text-center text-gray-400">
          Return to{' '}
          <Link
            to="/login"
            className="text-primary-deep focus:outline-none focus:underline hover:underline"
          >
            login
          </Link>
        </p>
      </div>
    </AuthLayout>
  );
};

export default ResetPassword;
